import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Scale, TriangleAlert, TrendingDown, TrendingUp } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatMoney } from "@/lib/prefs";

/**
 * LogPrepBatchDialog: glass panel where a chef enters the starting and
 * finished weights of a real prep batch. On save, the platform computes
 * the actual yield, the actual cost per usable kg, and, if the yield is
 * 5 or more points below the baseline, flags an alert. The saved batch
 * is used to override cost math on every recipe using that ingredient
 * for the next 7 days.
 */
export default function LogPrepBatchDialog({
  open,
  onOpenChange,
  ingredients,
  defaultIngredientId,
  onLogged,
}) {
  const [ingredientId, setIngredientId] = useState(defaultIngredientId || "");
  const [startingKg, setStartingKg] = useState("");
  const [finishedKg, setFinishedKg] = useState("");
  const [prepChef, setPrepChef] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setIngredientId(defaultIngredientId || "");
      setStartingKg("");
      setFinishedKg("");
      setPrepChef("");
      setNotes("");
    }
  }, [open, defaultIngredientId]);

  const ingredientOptions = useMemo(
    () => (ingredients || []).filter((i) => {
      const kind = (i.purchase_unit_kind || "").toLowerCase();
      return kind === "mass" || kind === "weight";
    }),
    [ingredients],
  );

  const selected = useMemo(
    () => (ingredients || []).find((i) => i.id === ingredientId),
    [ingredients, ingredientId],
  );

  const baselineYieldPct = Number(selected?.yield_percent ?? 100);

  const computed = useMemo(() => {
    const s = parseFloat(startingKg);
    const f = parseFloat(finishedKg);
    if (!Number.isFinite(s) || !Number.isFinite(f) || s <= 0 || f <= 0) return null;
    if (f > s * 1.001) return { invalid: "Finished weight cannot exceed starting weight." };
    const actualYield = (f / s) * 100;
    const delta = actualYield - baselineYieldPct;
    const isAlert = delta <= -5;
    return {
      actualYield,
      delta,
      isAlert,
    };
  }, [startingKg, finishedKg, baselineYieldPct]);

  const canSubmit = ingredientId && startingKg && finishedKg && !computed?.invalid && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post("/batch-yield/log", {
        ingredient_id: ingredientId,
        starting_weight_kg: parseFloat(startingKg),
        finished_weight_kg: parseFloat(finishedKg),
        prep_chef: prepChef.trim() || null,
        notes: notes.trim() || null,
      });
      if (data.is_yield_alert) {
        toast.error(`Low yield alert: ${data.actual_yield_percent}% (baseline ${data.baseline_yield_percent}%). Recipe margins updated for 7 days.`);
      } else {
        toast.success(`Batch logged: ${data.actual_yield_percent}% actual yield. Recipes will price with this cost for 7 days.`);
      }
      onOpenChange(false);
      if (onLogged) onLogged(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not log batch.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-white/10 max-w-lg" data-testid="batch-yield-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Scale className="w-5 h-5 text-amber-200" />
            Log a prep batch
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            Enter the real weights coming off the block. The platform will re-price every recipe using this ingredient for the next 7 days.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs text-white/60">Ingredient</Label>
            <Select value={ingredientId} onValueChange={setIngredientId}>
              <SelectTrigger data-testid="batch-yield-ingredient">
                <SelectValue placeholder="Pick a mass-based ingredient" />
              </SelectTrigger>
              <SelectContent>
                {ingredientOptions.length === 0 && (
                  <SelectItem value="__none__" disabled>
                    No mass-based ingredients yet
                  </SelectItem>
                )}
                {ingredientOptions.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.name} (baseline yield {Math.round(i.yield_percent || 100)}%)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-white/60">Starting weight (kg)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={startingKg}
                onChange={(e) => setStartingKg(e.target.value)}
                placeholder="10.0"
                className="tabular"
                data-testid="batch-yield-start"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-white/60">Finished weight (kg)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={finishedKg}
                onChange={(e) => setFinishedKg(e.target.value)}
                placeholder="6.2"
                className="tabular"
                data-testid="batch-yield-finish"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-white/60">Prep chef (optional)</Label>
              <Input
                value={prepChef}
                onChange={(e) => setPrepChef(e.target.value)}
                placeholder="Chef Rivera"
                data-testid="batch-yield-chef"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-white/60">Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Trimmed for tartare"
                data-testid="batch-yield-notes"
              />
            </div>
          </div>

          {computed?.invalid && (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200 flex items-center gap-2">
              <TriangleAlert className="w-3.5 h-3.5" />
              {computed.invalid}
            </div>
          )}

          {computed && !computed.invalid && (
            <div className="grid grid-cols-3 gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="batch-yield-preview">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50">Actual yield</div>
                <div className="font-display text-2xl tabular text-amber-100">
                  {computed.actualYield.toFixed(1)}%
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50">Baseline</div>
                <div className="font-display text-2xl tabular text-white/70">
                  {baselineYieldPct.toFixed(0)}%
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50">Delta</div>
                <div className={`font-display text-2xl tabular flex items-center gap-1 ${computed.delta >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                  {computed.delta >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {computed.delta >= 0 ? "+" : ""}{computed.delta.toFixed(1)}pt
                </div>
              </div>
              {computed.isAlert && (
                <div className="col-span-3 mt-1 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-100 flex items-center gap-2" data-testid="batch-yield-alert">
                  <TriangleAlert className="w-3.5 h-3.5" />
                  Yield is 5 or more points below baseline. Investigate trim technique, product quality, or supplier weight variance.
                </div>
              )}
            </div>
          )}

          {selected && (
            <div className="text-[11px] text-white/50">
              For reference: baseline cost snapshot is {formatMoney(selected.purchase_price)} per {selected.purchase_qty} {selected.purchase_unit} pack at {Math.round(selected.yield_percent || 100)}% baseline yield. The 7 day override starts the moment you save.
            </div>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="batch-yield-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="batch-yield-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scale className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Log batch</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
